// Чистая (DOM-независимая) логика показа текста ответа для
// captions_mode=after_playback — вынесена из avg-dialog.ts, чтобы её можно
// было юнит-тестировать эмуляцией последовательности SSE (tts_chunk → avatar
// → done) и колбэков плейбек-очереди (onEnded) без браузерного окружения.
//
// Исправляет дефект (подтверждён на проде 2026-08-02): текст раскрывался в
// момент СТАРТА пер-ходового видео (video_first), а не после его окончания.
// Причина была в том, что SSE 'done' раскрывал придержанный текст
// БЕЗУСЛОВНО, не проверяя, доиграло ли видео/аудио этого хода — совпадение
// по времени (done приходит почти сразу после кадра 'avatar', а play()
// стартует на следующем тике) маскировало это как «раскрытие на старте».
// Фикс: pendingPlaybackItems считает элементы очереди, на onEnded которых
// подписан revealHeldText (см. gatedPlaybackEnded); пока счётчик > 0,
// finishCaptionsStream (SSE 'done') не раскрывает текст — ждёт их onEnded.

export type CaptionsMode = 'immediate' | 'after_playback' | 'off';

/** Часть LiveTurnCtx (avg-dialog.ts), которой оперирует эта логика — сам
 * avg-dialog.ts держит более широкий контекст хода (answerParts, heldAudio,
 * turnVideoPlayed и т.п.), сюда прокидывается по ссылке. */
export interface CaptionsCtx {
  captionsMode: CaptionsMode;
  /** Текст юнитов, ждущих показа — уходит сюда вместо немедленного рендера,
   * дожидаясь onEnded воспроизведения (gatedPlaybackEnded/fallbackPlaybackEnded). */
  heldText: string[];
  /** Уже показанные (но не закоммиченные в историю) части — растущий «живой»
   * бабл, синхронный с воспроизведением. */
  revealedParts: string[];
  /** Финальный текст хода (приходит с SSE 'done'); пусто, пока ход не завершён. */
  finalText: string;
  /** 'done' уже пришёл — коммит в историю остаётся только за воспроизведением
   * (см. finishCaptionsStream/pendingPlaybackItems) либо происходит сразу. */
  streamDone: boolean;
  /** Ответ уже закоммичен в историю — коммитим не больше одного раза. */
  committed: boolean;
  /** Число ещё НЕ завершившихся элементов плейбек-очереди этого хода, на
   * onEnded которых подписан revealHeldText через gatedPlaybackEnded —
   * пер-ходовое видео (video_first, один элемент) либо юнит-клипы/аудио
   * (audio_first, несколько). Голосовой ФОЛБЭК (flushHeldAudio в
   * avg-dialog.ts, см. fallbackPlaybackEnded) сюда НЕ считается: по
   * требованию владельца деградация (турн-видео не пришло) показывает текст
   * сразу по 'done', не дожидаясь конца фолбэк-озвучки. */
  pendingPlaybackItems: number;
}

/** Побочные эффекты (DOM/история/плейбек), которые avg-dialog.ts
 * прокладывает сюда — сама эта логика ничего не знает о браузере. */
export type CaptionsSideEffects = {
  /** Показывает растущий «живой» бабл (join(' ') уже раскрытых частей). */
  renderLive: (joinedText: string) => void;
  /** Коммитит финальный текст хода в историю сообщений и снимает бабл. */
  commit: (text: string) => void;
  /** Голос аватара сейчас звучит (плейбек-очередь непуста/играет) — гейт
   * финального коммита (сам показ текста коммитом не гейтится). */
  isAvatarSpeaking: () => boolean;
};

export function createCaptionsCtx(captionsMode: CaptionsMode): CaptionsCtx {
  return {
    captionsMode,
    heldText: [],
    revealedParts: [],
    finalText: '',
    streamDone: false,
    committed: false,
    pendingPlaybackItems: 0,
  };
}

/** Придерживает текст юнита для показа после воспроизведения. No-op вне
 * after_playback (immediate/off решают показ иначе — см. avg-dialog.ts). */
export function holdUnitText(ctx: CaptionsCtx, text: string): void {
  if (ctx.captionsMode === 'after_playback') ctx.heldText.push(text);
}

/**
 * onEnded-колбэк для ОСНОВНОГО элемента плейбек-очереди этого хода —
 * пер-ходового видео (video_first) либо юнит-клипа/аудио (audio_first).
 * Инкрементирует pendingPlaybackItems в момент постановки в очередь и
 * декрементирует, когда элемент реально доиграл — пока счётчик > 0,
 * finishCaptionsStream (SSE 'done') не раскрывает текст раньше времени.
 * undefined вне after_playback — синхронизировать нечего.
 */
export function gatedPlaybackEnded(
  ctx: CaptionsCtx,
  fx: CaptionsSideEffects
): (() => void) | undefined {
  if (ctx.captionsMode !== 'after_playback') return undefined;
  ctx.pendingPlaybackItems++;
  return () => {
    ctx.pendingPlaybackItems--;
    revealHeldText(ctx, fx);
  };
}

/**
 * onEnded-колбэк для голосового ФОЛБЭКА (турн-видео не пришло —
 * flushHeldAudio в avg-dialog.ts): раскрывает/коммитит по мере доигрывания
 * фолбэк-озвучки, но НЕ считается в pendingPlaybackItems — деградация не
 * должна прятать текст ещё и до конца фолбэк-аудио (см. finishCaptionsStream).
 * undefined вне after_playback.
 */
export function fallbackPlaybackEnded(
  ctx: CaptionsCtx,
  fx: CaptionsSideEffects
): (() => void) | undefined {
  if (ctx.captionsMode !== 'after_playback') return undefined;
  return () => revealHeldText(ctx, fx);
}

/**
 * Раскрывает то, что успело придти (heldText → revealedParts, растущий
 * бабл) и, если ход уже завершился ('done' пришёл) и играть больше нечего
 * (isAvatarSpeaking() === false), коммитит финальный текст в историю.
 * Идемпотентна — безопасно вызывать повторно (в т.ч. после коммита).
 */
export function revealHeldText(ctx: CaptionsCtx, fx: CaptionsSideEffects): void {
  if (ctx.committed) return;
  if (ctx.heldText.length) {
    ctx.revealedParts.push(...ctx.heldText);
    ctx.heldText = [];
    fx.renderLive(ctx.revealedParts.join(' '));
  }
  if (ctx.streamDone && !fx.isAvatarSpeaking()) {
    commitCaptionsText(ctx, fx, ctx.finalText);
  }
}

/** Коммитит текст хода в историю. Идемпотентна (committed-флаг). */
export function commitCaptionsText(ctx: CaptionsCtx, fx: CaptionsSideEffects, text: string): void {
  if (ctx.committed) return;
  ctx.committed = true;
  fx.commit(text);
}

/**
 * SSE 'done': помечает ход завершённым и запоминает финальный текст.
 * Раскрывает НЕМЕДЛЕННО только если играть уже нечего
 * (pendingPlaybackItems === 0 — voice_only не пришёл, текстовый фолбэк,
 * ошибка рендера, либо всё уже доиграло раньше 'done'). Иначе ждёт onEnded
 * доигрывающих элементов (см. gatedPlaybackEnded) — это и есть исправление
 * дефекта «текст раскрывался на старте видео вместо конца».
 */
export function finishCaptionsStream(
  ctx: CaptionsCtx,
  fx: CaptionsSideEffects,
  finalText: string
): void {
  ctx.finalText = finalText;
  ctx.streamDone = true;
  if (ctx.pendingPlaybackItems === 0) revealHeldText(ctx, fx);
}

/**
 * Barge-in/«стоп»/обрыв стрима: придержанный текст не должен потеряться —
 * коммитим то, что успело прийти, немедленно, вместо того чтобы ждать
 * доигрывания (которое вызывающая сторона сама же обрывает stopLivePlayback()).
 * No-op вне after_playback или если уже закоммичено.
 */
export function flushCaptionsOnInterrupt(
  ctx: CaptionsCtx,
  fx: CaptionsSideEffects,
  fallbackText: string
): void {
  if (ctx.captionsMode !== 'after_playback' || ctx.committed) return;
  const combined = [...ctx.revealedParts, ...ctx.heldText].join(' ') || fallbackText;
  ctx.heldText = [];
  ctx.revealedParts = [];
  commitCaptionsText(ctx, fx, combined);
}
